import { useCallback, useMemo } from 'react';

import { useAnalyticsContext } from './context';

type TrackFn = (eventName: string, data: Record<string, unknown>) => void;

type AccountsTracker = {
  detailViewed: (params: {
    accountId: string;
    linkId?: string | null;
    currency?: string | null;
  }) => void;

  tagEdited: (params: {
    accountId?: string | null;
    isBulk: boolean;
    selectedCount?: number;
  }) => void;

  ownershipEdited: (params: {
    accountId: string;
  }) => void;

  balanceLimitEdited: (params: {
    accountId: string;
    currency?: string | null;
  }) => void;

  cashHoldingEdited: (params: {
    accountId: string;
    linkId?: string | null;
  }) => void;

  accountGroupEdited: (params: {
    accountId: string;
    accountGroupId?: string | null;
  }) => void;

  deleted: (params: {
    accountId: string;
    linkId?: string | null;
    status?: string | null;
  }) => void;

  favouriteToggled: (params: {
    accountId: string;
    isFavourite: boolean;
  }) => void;
};

export const useAccountsTracking = (): AccountsTracker => {
  const analytics = useAnalyticsContext();

  const track: TrackFn = useCallback((eventName, data) => {
    try {
      analytics?.mixPanel?.trackContinueEvent?.({
        customEvent: eventName,
        module: 'accounts',
        ...data,
      });
    } catch {
      // Tracking must never break the UI.
    }
  }, [analytics]);

  return useMemo<AccountsTracker>(() => ({
    detailViewed: (params) => track('account_detail_viewed', params),
    tagEdited: (params) => track('account_tag_edited', params),
    ownershipEdited: (params) => track('account_ownership_edited', params),
    balanceLimitEdited: (params) => track('account_balance_limit_edited', params),
    cashHoldingEdited: (params) => track('account_cash_holding_edited', params),
    accountGroupEdited: (params) => track('account_group_edited', params),
    deleted: (params) => track('account_deleted', params),
    favouriteToggled: (params) => track('account_favourite_toggled', params),
  }), [track]);
};
